import { z } from 'zod';

/**
 * Where one verification attempt stands (`03-verification.md`).
 *
 * This machine is **separate from the member lifecycle** (N9): `users.status`
 * knows only that a member is `pending_verification`, and every substate of the
 * review lives here. That separation is what lets a reviewer ask for a new
 * selfie without moving the member out of the verification stack the mobile root
 * navigator put them in (M7).
 *
 * - `draft` — materialized, awaiting the member's evidence;
 * - `submitted` — the member has done their part; it is in the queue;
 * - `in_review` — a reviewer has picked it up;
 * - `changes_requested` — specific steps were sent back; the member resubmits;
 * - `approved` / `rejected` — **decided, and terminal for this case**. A member
 *   who may try again gets a *new* case with the next `attemptNumber`, so the
 *   refused attempt's decision trail survives intact.
 */
export const VERIFICATION_CASE_STATUSES = [
  'draft',
  'submitted',
  'in_review',
  'changes_requested',
  'approved',
  'rejected',
] as const;

export const VerificationCaseStatusSchema = z.enum(VERIFICATION_CASE_STATUSES);

export type VerificationCaseStatus = (typeof VERIFICATION_CASE_STATUSES)[number];

/** What case creation writes — evidence has not been asked for yet. */
export const INITIAL_CASE_STATUS: VerificationCaseStatus = 'draft';

const DECIDED: ReadonlySet<VerificationCaseStatus> = new Set<VerificationCaseStatus>([
  'approved',
  'rejected',
]);

/** A decision has been recorded; nothing further happens to this case. */
export function isDecidedCaseStatus(status: VerificationCaseStatus): boolean {
  return DECIDED.has(status);
}

const MEMBER_HELD: ReadonlySet<VerificationCaseStatus> = new Set<VerificationCaseStatus>([
  'draft',
  'changes_requested',
]);

/**
 * The member, not the queue, holds the case — the only states in which they may
 * change what it contains.
 *
 * Both halves of "the member may edit this" read it: sending the case
 * (`submitCase`) and binding evidence to one step (`submitStep`). They were
 * written as two separate conditions once, and the step half was simply missing
 * — which let a member swap a document out from under a reviewer already reading
 * it.
 */
export function isMemberHeldCaseStatus(status: VerificationCaseStatus): boolean {
  return MEMBER_HELD.has(status);
}

/**
 * The case is still live.
 *
 * The **partial unique index** that enforces "one open case per member" is built
 * from exactly these statuses, so this predicate and that index are one fact
 * with two spellings — a status counted open here and omitted there would let a
 * member accumulate live cases.
 */
export function isOpenCaseStatus(status: VerificationCaseStatus): boolean {
  return !isDecidedCaseStatus(status);
}
